/**
 * useSpeechToText.js
 * ==================
 * Lightweight React hook around the Web Speech API (browser-native STT).
 *
 * Why Web Speech API:
 *   - Zero API key, zero per-request cost
 *   - Native on iOS Safari 14+, Android Chrome, desktop Chrome/Edge
 *   - Multi-lingual (en-US/en-GB/hi-IN/es-ES/fr-FR/de-DE/it-IT)
 *   - Returns a single final transcript per session — perfect for our
 *     "tap → speak → words appear in the input box" UX
 *
 * When unsupported (Firefox, older Safari), `supported === false` and the
 * caller hides the mic button entirely.
 *
 * Doctrine: voice is an alternative INPUT mode, not a primary command
 * channel. The transcript fills the existing input box; the member can
 * edit before sending. Mira sees plain text — she doesn't know it was
 * spoken.
 */
import { useEffect, useRef, useState, useCallback } from "react";

// Pick the appropriate vendor-prefixed constructor.
function getSR() {
  if (typeof window === "undefined") return null;
  return (
    window.SpeechRecognition ||
    window.webkitSpeechRecognition ||
    null
  );
}

const DEFAULT_LANG_GUESS = () => {
  if (typeof navigator === "undefined") return "en-US";
  const raw = (navigator.language || "en-US").trim();
  // Web Speech API expects forms like 'en-US', 'hi-IN', 'fr-FR'
  return raw.replace("_", "-");
};

export function useSpeechToText({
  language,
  onResult,
  onError,
  continuous = false,
  interimResults = true,
} = {}) {
  const SR = getSR();
  const supported = !!SR;

  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);

  // Lazy-init the recogniser the first time `start()` is called.
  const ensureRecogniser = useCallback(() => {
    if (!supported) return null;
    if (recRef.current) return recRef.current;
    const r = new SR();
    r.continuous = continuous;
    r.interimResults = interimResults;
    r.lang = language || DEFAULT_LANG_GUESS();
    r.maxAlternatives = 1;
    r.onstart = () => {
      setError(null);
      setListening(true);
    };
    r.onend = () => {
      setListening(false);
      setInterim("");
    };
    r.onerror = (e) => {
      // Permission denied / no-mic / aborted etc. — surface to the UI
      const code = e?.error || "unknown";
      setError(code);
      setListening(false);
      setInterim("");
      onError && onError(code, e);
    };
    r.onresult = (e) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const txt = res[0]?.transcript || "";
        if (res.isFinal) finalChunk += txt;
        else interimChunk += txt;
      }
      if (interimChunk) setInterim(interimChunk);
      if (finalChunk && onResult) {
        // Trim leading whitespace + capitalise first letter for niceness
        const cleaned = finalChunk
          .trim()
          .replace(/^./, (c) => c.toUpperCase());
        onResult(cleaned);
        setInterim("");
      }
    };
    recRef.current = r;
    return r;
  }, [SR, supported, language, continuous, interimResults, onResult, onError]);

  const start = useCallback(() => {
    const r = ensureRecogniser();
    if (!r) return;
    // If already listening, ignore the tap (prevents InvalidStateError)
    if (listening) return;
    try {
      r.lang = language || DEFAULT_LANG_GUESS();
      r.start();
    } catch (e) {
      // Most common: 'InvalidStateError' if start is called twice quickly
      setError(e?.name || "start_failed");
    }
  }, [ensureRecogniser, listening, language]);

  const stop = useCallback(() => {
    const r = recRef.current;
    if (!r) return;
    try {
      r.stop();
    } catch (_e) { /* swallow — already stopped */ }
  }, []);

  // Clean up on unmount — prevents the mic LED staying on if the user
  // navigates away mid-listen.
  useEffect(() => () => {
    const r = recRef.current;
    if (r) {
      try { r.abort(); } catch (_e) { /* */ }
    }
  }, []);

  return { supported, listening, interim, error, start, stop };
}
